import type { MfaData } from '#root/api/clients/authentication/data-contracts'
import type { FormLocation } from '#types/gtm'
import { getPkceCodePair } from '#core/utils/auth'
import { EnrollmentState } from '#root/enums/profile'

export default (
  email: string,
  {
    onContinue,
    onError,
    onSuccess,
    formLocation = 'modal:single:none:none'
  }:
  {
    onContinue: (state: EnrollmentState) => void
    onError: (err: Error) => void
    onSuccess: () => void
    formLocation?: FormLocation
  }
) => {
  const recaptcha = useRecaptcha()
  const { authentication } = useApi()
  const { passkeyLogin, postLoginActions } = useAuthStore()
  const { $gtm } = useNuxtApp()
  const user = useUserStore()

  const signInWithPasskey = async () => {
    const { codeChallenge } = await getPkceCodePair()
    const captchaResponse = await recaptcha.execute('login')
    try {
      let response: Extract<MfaData, { publicKey: any }>

      try {
        response = await authentication.$mfa({
          authMethod: 'passkey',
          action: 'signin',
          username: email,
          codeChallenge,
          recaptcha_response: captchaResponse,
        }) as Extract<MfaData, { publicKey: any }>

        const numAllowCredentials = response.publicKey.allowCredentials?.length ?? 0

        if (!numAllowCredentials) {
        // No credentials registered, redirect to create passkey flow
          return onContinue(EnrollmentState.CREATE_PASSKEY)
        }
      }
      catch (err) {
        assertApiError(err)

        if (err.response?.status === 412) {
          // Passkey has not been registered on the server, redirect to the create passkey flow
          return onContinue(EnrollmentState.CREATE_PASSKEY)
        }

        throw err
      }

      // attempt sign in with existing passkeys
      const assertion = await navigator.credentials.get({
        publicKey: {
          ...response.publicKey,
          challenge: base64URLToBuffer(response.publicKey.challenge),
          allowCredentials: (response.publicKey.allowCredentials ?? []).map((cred) => ({
            ...cred,
            id: base64URLToBuffer(cred.id),
          })),
        },
      }) as PublicKeyCredential | null

      if (!assertion) {
        return onContinue(EnrollmentState.CREATE_PASSKEY)
      }

      const assertionResponse = assertion.response as AuthenticatorAssertionResponse

      await passkeyLogin({
        credentialId: assertion.id,
        authenticatorData: bufferToBase64URL(assertionResponse.authenticatorData),
        clientDataJSON: bufferToBase64URL(assertionResponse.clientDataJSON),
        signature: bufferToBase64URL(assertionResponse.signature),
        userHandle: assertionResponse.userHandle ? bufferToBase64URL(assertionResponse.userHandle) : undefined
      })
      await user.get()
      await postLoginActions().catch((err) => log.error(err))
      $gtm.push('authForm.onLoginConfirmed', formLocation, 'Passkey', false)
      onSuccess()
    }
    catch (err) {
      onError(err as Error)
      log.error(err)
    }
  }

  return {
    signInWithPasskey
  }
}
